import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Source as SourceIcon
} from '@mui/icons-material';
import AdminLayout from '../../components/templates/layout/AdminLayout';
import api from '../../services/api';

const sourceTypeLabels = {
  online: 'Online',
  social: 'Rede social',
  print: 'Impresso',
  radio: 'Radio',
  tv: 'TV',
  podcast: 'Podcast'
};

const sourceGroupLabels = {
  online_portal: 'Online / Portal',
  traditional: 'Tradicional',
  social_mass: 'Social Mass',
  social_info: 'Social Info',
  social_short: 'Social Short'
};

const statusLabels = {
  pending: 'Pendente',
  validated: 'Validado',
  rejected: 'Rejeitado'
};

const shouldShowPlatform = (source) => (
  source.platform &&
  source.platform !== 'Online' &&
  source.platform !== sourceTypeLabels[source.sourceType]
);

const formatNumber = (value) => Number(value || 0).toLocaleString('pt-BR');

const formatCurrency = (value) => Number(value || 0).toLocaleString('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  maximumFractionDigits: 2
});

const SourceRegistryPage = () => {
  const [sources, setSources] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sourceType, setSourceType] = useState('all');
  const [sourceGroup, setSourceGroup] = useState('all');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 400);

    return () => clearTimeout(timer);
  }, [search]);

  const fetchSources = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [sourcesRes, statsRes] = await Promise.all([
        api.get('/source-registry', {
          params: {
            page: page + 1,
            limit: rowsPerPage,
            search: debouncedSearch || undefined,
            sourceType,
            sourceGroup,
            status
          }
        }),
        api.get('/source-registry/stats')
      ]);

      setSources(sourcesRes.data.data || []);
      setTotal(sourcesRes.data.pagination?.total || 0);
      setStats(statsRes.data.data || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao carregar o cadastro de fontes');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, sourceType, sourceGroup, status]);

  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  const handleRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <AdminLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <SourceIcon color="primary" />
              <Typography variant="h5" fontWeight={700}>
                Cadastro Central de Fontes
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Leitura da nova base paralela de fontes online, sociais e tradicionais.
            </Typography>
          </Box>
          <Tooltip title="Atualizar">
            <span>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchSources} disabled={loading}>
                Atualizar
              </Button>
            </span>
          </Tooltip>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Total</Typography>
            <Typography variant="h5" fontWeight={700}>{formatNumber(stats?.total)}</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Online</Typography>
            <Typography variant="h5" fontWeight={700}>{formatNumber(stats?.byType?.online)}</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Redes sociais</Typography>
            <Typography variant="h5" fontWeight={700}>{formatNumber(stats?.byType?.social)}</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Impressos</Typography>
            <Typography variant="h5" fontWeight={700}>{formatNumber(stats?.byType?.print)}</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Radio/TV/Podcast</Typography>
            <Typography variant="h5" fontWeight={700}>
              {formatNumber((stats?.byType?.radio || 0) + (stats?.byType?.tv || 0) + (stats?.byType?.podcast || 0))}
            </Typography>
          </Paper>
        </Stack>

        <Paper variant="outlined">
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ p: 2 }}>
            <TextField
              size="small"
              placeholder="Buscar por domínio, perfil ou nome..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{ minWidth: { xs: '100%', lg: 320 } }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Tipo</InputLabel>
              <Select label="Tipo" value={sourceType} onChange={(event) => { setSourceType(event.target.value); setPage(0); }}>
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="online">Online</MenuItem>
                <MenuItem value="social">Rede social</MenuItem>
                <MenuItem value="print">Impresso</MenuItem>
                <MenuItem value="radio">Radio</MenuItem>
                <MenuItem value="tv">TV</MenuItem>
                <MenuItem value="podcast">Podcast</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel>Grupo</InputLabel>
              <Select label="Grupo" value={sourceGroup} onChange={(event) => { setSourceGroup(event.target.value); setPage(0); }}>
                <MenuItem value="all">Todos</MenuItem>
                {Object.entries(sourceGroupLabels).map(([value, label]) => (
                  <MenuItem value={value} key={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={status} onChange={(event) => { setStatus(event.target.value); setPage(0); }}>
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="pending">Pendente</MenuItem>
                <MenuItem value="validated">Validado</MenuItem>
                <MenuItem value="rejected">Rejeitado</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <TableContainer sx={{ maxHeight: 680 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fonte</TableCell>
                  <TableCell>Tipo / Grupo</TableCell>
                  <TableCell>Categorias</TableCell>
                  <TableCell align="right">Audiência / Seguidores</TableCell>
                  <TableCell align="right">CPC</TableCell>
                  <TableCell align="right">Valoração</TableCell>
                  <TableCell align="center">Tiers P/M/G</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : sources.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      Nenhuma fonte encontrada
                    </TableCell>
                  </TableRow>
                ) : sources.map((source) => (
                  <TableRow key={source._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>{source.siteName || source.domain || source.username}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {source.sourceType === 'social' ? `@${source.username}` : source.domain}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Chip size="small" label={sourceTypeLabels[source.sourceType] || source.sourceType} />
                        {shouldShowPlatform(source) && (
                          <Chip size="small" variant="outlined" label={source.platform} />
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {sourceGroupLabels[source.sourceGroup] || source.sourceGroup}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {(source.categories || []).slice(0, 3).map((category) => (
                          <Chip size="small" key={category} label={category} />
                        ))}
                        {(source.categories || []).length > 3 && (
                          <Chip size="small" label={`+${source.categories.length - 3}`} />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {source.sourceType === 'social'
                          ? formatNumber(source.followers)
                          : formatNumber(source.audienceDaily)}
                      </Typography>
                      {source.sourceType !== 'social' && (
                        <Typography variant="caption" color="text.secondary">
                          mensal {formatNumber(source.audienceMonthly)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{source.cpc ? formatCurrency(source.cpc) : '-'}</TableCell>
                    <TableCell align="right">{source.valuation ? formatCurrency(source.valuation) : '-'}</TableCell>
                    <TableCell align="center">
                      {source.tierSmall || 3}/{source.tierMedium || 3}/{source.tierLarge || 3}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={statusLabels[source.status] || source.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={handleRowsPerPage}
            labelRowsPerPage="Linhas por página"
          />
        </Paper>
      </Box>
    </AdminLayout>
  );
};

export default SourceRegistryPage;
